import { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

import {
  namespaces,
  useSharedTranslation,
} from 'us-react-components/dist/hooks/useSharedTranslation';
import { Modal } from 'us-react-components/dist/Modal/Modal';
import { formatDateRange, getMonthDayString } from 'us-react-components/dist/utils/date';
import {
  axios,
  Job,
  JobService,
  SitterService,
  UserGeocodeService,
} from 'us-web-services';
import { Button, ButtonType } from '../../components';
import { useDependencies } from '../../dependencies';

const ZIP_REGEX = /^\d{5}$/;
const JOB_STATUS_OPEN = 'open';
const THREE_CALENDARS_ICON =
  'https://assets.urbansitter.com/static/img/icons/calendar-three-white.svg';
const RIGHT_GRADIENT_LG =
  'https://assets.urbansitter.com/static/img/backgrounds/right-primary-gradient-lg.webp';
const PHONE_LOCATION_LG =
  'https://assets.urbansitter.com/static/img/backgrounds/phone-location-lg.png';

interface AddressShape {
  street?: string;
  street2?: string;
  city?: string;
  state?: string;
  zip?: string;
  neighborhood?: string;
  latitude?: number;
  longitude?: number;
}

const getChildrenLine = (booking?: {
  numberOfChildren?: number;
  kidsAges?: string;
  type?: string;
}): string => {
  if (!booking) return '';
  const n = booking.numberOfChildren ?? 0;
  switch (booking.type) {
    case 'petCare':
      return `${n} ${n === 1 ? 'pet' : 'pets'}`;
    case 'seniorCare':
      return `${n} ${n === 1 ? 'senior' : 'seniors'}`;
    case 'householdServices':
      return '';
    default:
      return `${n} ${n === 1 ? 'child' : 'children'}${
        booking.kidsAges ? ` (${booking.kidsAges})` : ''
      }`;
  }
};

export const Location = observer((): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const navigate = useNavigate();
  const { stores } = useDependencies();
  const sitter = stores.sitter.sitter;

  const initial: AddressShape = (sitter?.address as AddressShape) ?? {};
  const [street, setStreet] = useState<string>(initial.street ?? '');
  const [street2, setStreet2] = useState<string>(initial.street2 ?? '');
  const [zip, setZip] = useState<string>(initial.zip ?? '');
  const [city, setCity] = useState<string>(initial.city ?? '');
  const [state, setState] = useState<string>(initial.state ?? '');
  const [neighborhood, setNeighborhood] = useState<string>(initial.neighborhood ?? '');
  const [lat, setLat] = useState<number | undefined>(initial.latitude);
  const [lng, setLng] = useState<number | undefined>(initial.longitude);
  const [submitting, setSubmitting] = useState(false);
  const [touched, setTouched] = useState(false);
  const [openJobs, setOpenJobs] = useState<Job[]>([]);
  const [showJobModal, setShowJobModal] = useState(false);
  const streetInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    document.title = 'UrbanSitter - Your Location';
    // Preserve the legacy autoFocus-on-mount behaviour while keeping lint happy.
    streetInputRef.current?.focus();
  }, []);

  const onZipChange = async (next: string): Promise<void> => {
    setZip(next);
    if (!ZIP_REGEX.test(next) || !sitter?.id) {
      return;
    }
    try {
      const resp = (await UserGeocodeService.create(sitter.id, { zip: next } as never)).data;
      const geo = resp?.data ?? null;
      if (!geo) return;
      if (!city) setCity(geo.city ?? '');
      if (!state) setState(geo.state ?? '');
      setLat(geo.latitude);
      setLng(geo.longitude);
    } catch (err) {
      console.warn('[SitterLocation] geocode failed', err);
    }
  };

  // Match Angular's getOpenJobs: geocode + query /v2/jobs for 10 nearest open jobs.
  useEffect(() => {
    if (!sitter?.id) return;
    if (!street.trim() || !city.trim() || !state.trim() || !ZIP_REGEX.test(zip)) {
      setOpenJobs([]);
      return;
    }
    let cancelled = false;
    const load = async (): Promise<void> => {
      try {
        const geoResp = await UserGeocodeService.create(sitter.id, {
          id: sitter.id,
          city,
          state,
          street,
          zip,
        } as never);
        const geo = geoResp.data?.data;
        if (!geo?.latitude || !geo?.longitude) return;
        const jobResp = await JobService.getByFilter({
          params: {
            distance: 50,
            limit: 10,
            latitude: geo.latitude,
            longitude: geo.longitude,
            sort: 'updated',
            status: JOB_STATUS_OPEN,
          },
        });
        if (cancelled) return;
        setOpenJobs(jobResp.data?.data ?? []);
      } catch (err) {
        console.warn('[SitterLocation] open jobs load failed', err);
      }
    };
    void load();
    return (): void => {
      cancelled = true;
    };
  }, [sitter?.id, street, city, state, zip]);

  const jobsVisible = openJobs.length > 3;

  const validStreet = !!street.trim();
  const validZip = ZIP_REGEX.test(zip);
  const validCity = !!city.trim();
  const validState = !!state.trim();

  const canSubmit =
    !submitting && validStreet && validZip && validCity && validState;

  const submit = async (): Promise<void> => {
    setTouched(true);
    if (!sitter?.id || !canSubmit) return;
    setSubmitting(true);
    try {
      await SitterService.patch(sitter.id, {
        id: sitter.id,
        address: {
          street,
          street2,
          city,
          state,
          zip,
          neighborhood,
          latitude: lat,
          longitude: lng,
        },
      } as never);
      await stores.sitter.refreshSitter?.();
      navigate('/sitter/registration/photo');
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.data?.errors?.[0]?.message) {
        toast.error(e.response.data.errors[0].message);
      } else {
        toast.error(t('pages.sitterLocation.submitFailed'));
      }
      setSubmitting(false);
    }
  };

  const fieldClass = (invalid: boolean): string =>
    `form-control w-full border rounded px-3 py-2 ${
      invalid ? 'border-red-500' : 'border-neutral-300'
    }`;

  return (
    <div className='relative overflow-hidden md:min-h-[560px]'>
      <div
        aria-hidden='true'
        className='hidden md:block absolute top-0 right-0 h-full bg-no-repeat bg-right-top pointer-events-none'
        style={{
          backgroundImage: `url(${RIGHT_GRADIENT_LG})`,
          backgroundSize: '100% 100%',
          width: jobsVisible ? '35%' : '50%',
        }}
      />
      <div
        aria-hidden='true'
        className='hidden md:block absolute top-[30px] h-[500px] w-[50%] bg-no-repeat bg-top bg-contain pointer-events-none'
        style={{
          backgroundImage: `url(${PHONE_LOCATION_LG})`,
          right: jobsVisible ? '-100px' : 0,
        }}
      />
      <div className='relative container py-8 max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-6'>
      <div className={`${jobsVisible ? 'lg:col-span-4' : 'lg:col-span-6'} us-card rounded p-6`}>
        <h1 className='text-2xl font-semibold mb-6'>{t('pages.sitterLocation.heading')}</h1>

        <div className='grid grid-cols-4 gap-3 mb-5'>
          <div className='col-span-3'>
            <label htmlFor='address-street' className='block text-sm mb-1'>
              {t('pages.sitterLocation.streetLabel')}
            </label>
            <input
              ref={streetInputRef}
              id='address-street'
              type='text'
              className={fieldClass(!validStreet && touched)}
              placeholder={t('pages.sitterLocation.streetPlaceholder')}
              value={street}
              onChange={(e): void => setStreet(e.target.value)}
              required
            />
          </div>
          <div>
            <label htmlFor='address-street2' className='block text-sm mb-1'>
              {t('pages.sitterLocation.street2Label')}
            </label>
            <input
              id='address-street2'
              type='text'
              className={fieldClass(false)}
              placeholder={t('pages.sitterLocation.street2Placeholder')}
              value={street2}
              onChange={(e): void => setStreet2(e.target.value)}
            />
          </div>
        </div>

        <div className='grid grid-cols-3 gap-3 mb-5'>
          <div>
            <label htmlFor='address-zip' className='block text-sm mb-1'>
              {t('pages.sitterLocation.zipLabel')}
            </label>
            <input
              id='address-zip'
              type='tel'
              className={fieldClass(!validZip && touched)}
              placeholder={t('pages.sitterLocation.zipPlaceholder')}
              value={zip}
              onChange={(e): Promise<void> => onZipChange(e.target.value)}
              maxLength={5}
              required
            />
          </div>
          <div className='col-span-2'>
            <label htmlFor='address-city' className='block text-sm mb-1'>
              {t('pages.sitterLocation.cityLabel')}
            </label>
            <input
              id='address-city'
              type='text'
              className={fieldClass(!validCity && touched)}
              placeholder={t('pages.sitterLocation.cityPlaceholder')}
              value={city}
              onChange={(e): void => setCity(e.target.value)}
            />
          </div>
        </div>

        <div className='grid grid-cols-3 gap-3 mb-8'>
          <div>
            <label htmlFor='address-state' className='block text-sm mb-1'>
              {t('pages.sitterLocation.stateLabel')}
            </label>
            <input
              id='address-state'
              type='text'
              maxLength={2}
              className={fieldClass(!validState && touched)}
              placeholder={t('pages.sitterLocation.statePlaceholder')}
              value={state}
              onChange={(e): void => setState(e.target.value.toUpperCase())}
              required
            />
          </div>
          <div className='col-span-2'>
            <label htmlFor='address-neighborhood' className='block text-sm mb-1'>
              {t('pages.sitterLocation.neighborhoodLabel')}
            </label>
            <input
              id='address-neighborhood'
              type='text'
              className={fieldClass(false)}
              placeholder={t('pages.sitterLocation.neighborhoodPlaceholder')}
              value={neighborhood}
              onChange={(e): void => setNeighborhood(e.target.value)}
            />
          </div>
        </div>

        <div className='text-center'>
          <Button
            id='submit-location'
            buttonType={ButtonType.primary}
            onClick={submit}
            disabled={submitting}
          >
            {t('pages.sitterLocation.continue')}
          </Button>
          <div className='flex justify-center gap-2 mt-4'>
            {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
              <span
                key={i}
                className={`w-2 h-2 rounded-full inline-block ${
                  i === 1 ? 'bg-sapphire' : 'border border-neutral-300'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {jobsVisible && (
        <aside className='lg:col-span-4 lg:col-start-5 space-y-4 max-h-[600px] overflow-y-auto pr-1'>
          {openJobs.map((job) => {
            const booking = job.booking;
            const parent = booking?.parent;
            const desc = booking?.description ?? '';
            const truncatedDesc = desc.length > 36 ? `${desc.slice(0, 36)}...` : desc;
            const title = job.recurring
              ? `${job.availability ?? ''}, Starting ${getMonthDayString(booking?.start)}`
              : formatDateRange(booking?.start, booking?.end);
            return (
              <div
                key={job.id}
                role='button'
                tabIndex={0}
                className='bg-white border border-neutral-200 shadow-sm rounded-lg p-4 cursor-pointer hover:shadow-md hover:border-sapphire transition-shadow'
                onClick={(): void => setShowJobModal(true)}
                onKeyDown={(e): void => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setShowJobModal(true);
                  }
                }}
              >
                <div className='flex gap-3'>
                  {parent?.pictureUrl && (
                    <img
                      alt={parent.firstName ?? ''}
                      src={parent.pictureUrl}
                      className='w-16 h-16 rounded-full object-cover flex-shrink-0'
                    />
                  )}
                  <div className='flex-1 min-w-0 text-sm'>
                    <div className='font-semibold text-neutral-900'>{title}</div>
                    <div className='text-neutral-700'>
                      {booking?.rate ? `$${booking.rate}/hr` : t('pages.sitterLocation.rateNegotiable')}
                      {booking?.rate && booking?.paymentType && (
                        <span className='text-neutral-500'> via {booking.paymentType}</span>
                      )}
                    </div>
                    <div className='text-neutral-600'>{booking?.address?.city}</div>
                    <div className='text-neutral-600'>{getChildrenLine(booking)}</div>
                  </div>
                </div>
                {truncatedDesc && (
                  <div className='mt-3 pt-3 border-t border-neutral-100 text-sm text-neutral-700'>
                    {truncatedDesc}
                  </div>
                )}
              </div>
            );
          })}
          <div
            role='button'
            tabIndex={0}
            className='bg-white border border-neutral-200 shadow-sm rounded-lg text-center p-6 cursor-pointer text-lg hover:shadow-md hover:border-sapphire transition-shadow'
            onClick={(): void => setShowJobModal(true)}
            onKeyDown={(e): void => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setShowJobModal(true);
              }
            }}
          >
            <img
              src={THREE_CALENDARS_ICON}
              alt={t('pages.sitterLocation.andMore')}
              className='mx-auto mb-3'
            />
            {t('pages.sitterLocation.andMore')}
          </div>
        </aside>
      )}

      <Modal
        isOpen={showJobModal}
        onClose={(): void => setShowJobModal(false)}
        title={t('pages.sitterLocation.modalHeading')}
        className='w-full max-w-md'
      >
        <p className='mb-5'>{t('pages.sitterLocation.modalBody')}</p>
        <div className='flex gap-3'>
          <Button
            buttonType={ButtonType.primary}
            onClick={(): void => {
              setShowJobModal(false);
              submit();
            }}
            disabled={submitting}
          >
            {t('pages.sitterLocation.saveAndContinue')}
          </Button>
          <Button
            buttonType={ButtonType.secondary}
            onClick={(): void => setShowJobModal(false)}
          >
            {t('common.cancel')}
          </Button>
        </div>
      </Modal>
      </div>
    </div>
  );
});
